/**
 * 积分状态 Hook
 * 管理积分余额查询、自动刷新和跨窗口同步
 * 支持错误重试、智能缓存和性能优化
 */

import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import pointsService, {
  GetPointsHistoryParams,
} from "@/services/pointsService";
import { pointsKeys } from "@/lib/pointsQueryClient";

// ============ UsePoints (余额) ============

interface UsePointsOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
  enabled?: boolean; // 允许禁用查询
}

export function usePoints(options: UsePointsOptions = {}) {
  const {
    autoRefresh = true,
    refreshInterval = 30000, // 30秒自动刷新
    enabled = true,
  } = options;

  return useQuery({
    queryKey: pointsKeys.balance(),
    queryFn: () => pointsService.getBalance(),

    // 启用/禁用查询
    enabled,

    // 自动刷新 (30秒)
    refetchInterval: autoRefresh ? refreshInterval : false,

    // 窗口聚焦时刷新 (跨标签页同步)
    refetchOnWindowFocus: true,

    // 数据保持新鲜的时间 (10秒内不重复请求)
    staleTime: 10000,

    // 缓存时间 (5分钟后过期清理)
    gcTime: 5 * 60 * 1000,

    // 错误重试策略: 最多重试2次，指数退避
    retry: (failureCount, error) => {
      // 客户端错误(4xx)不重试
      if (error instanceof Error && "status" in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

// ============ UsePointsHistory (历史记录) ============

interface UsePointsHistoryOptions {
  pageSize?: number;
  enabled?: boolean;
}

export function usePointsHistory(options: UsePointsHistoryOptions = {}) {
  const { pageSize = 10, enabled = true } = options;

  return useInfiniteQuery({
    queryKey: pointsKeys.history({ pageSize }),
    queryFn: ({ pageParam = 1 }) =>
      pointsService.getHistory({
        page: pageParam as number,
        per_page: pageSize,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.meta.has_more ? lastPage.meta.current_page + 1 : undefined,
    initialPageParam: 1,

    // 启用/禁用查询
    enabled,

    // 窗口聚焦时刷新第一页，确保看到最新记录
    refetchOnWindowFocus: true,

    // 首页数据保持新鲜15秒
    staleTime: 15000,

    // 缓存时间 (10分钟后过期清理)
    gcTime: 10 * 60 * 1000,

    // 错误重试策略
    retry: (failureCount, error) => {
      if (error instanceof Error && "status" in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
